import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  EventSeat,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  CalendarToday,
  Repeat,
  Schedule,
  LockOutlined,
} from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import {
  formatHours,
  formatOperationalDays,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  MotionList,
  MotionItem,
  AnimatedNumber,
} from './themeHelpers';

// Placeholder images
const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

function ModernTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const shouldReduce = useReducedMotion();

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;

  const defaultDescription = `Welcome to ${library.name}! We provide a peaceful and productive environment for students and professionals. Visit us to experience our top-notch facilities and quiet study spaces.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);

  // Seat availability display
  const availSeats = library.available_seats ?? library.capacity;
  const seatLabel =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity} free`
      : library.capacity
      ? `${library.capacity} seats`
      : null;

  // AnimatedNumber values — only animate if it's a clean integer
  const availSeatsNum =
    typeof availSeats === 'number' && Number.isFinite(availSeats) ? availSeats : null;
  const lockerNum =
    library.locker_count > 0 && typeof library.locker_count === 'number'
      ? library.locker_count
      : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '50vh', md: '70vh' },
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt={library.name}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7))',
          }}
        />

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevImage}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={nextImage}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: 'white' },
              }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        {/* Hero Content — fade+slide-up on mount */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: { xs: 3, md: 6 },
            color: 'white',
          }}
        >
          <Container maxWidth="lg">
            {shouldReduce ? (
              <>
                <Typography variant="h2" fontWeight={700} sx={{ mb: 1, fontSize: { xs: '2rem', md: '3.5rem' } }}>
                  {library.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOn />
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>{library.address}</Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => onBookNow()}
                  sx={{
                    bgcolor: accentColor,
                    '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                  }}
                >
                  Book Now
                </Button>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
              >
                <Typography variant="h2" fontWeight={700} sx={{ mb: 1, fontSize: { xs: '2rem', md: '3.5rem' } }}>
                  {library.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOn />
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>{library.address}</Typography>
                </Box>
                <motion.div
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.45, ease: 'easeOut' }}
                  style={{ display: 'inline-block' }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => onBookNow()}
                    sx={{
                      bgcolor: accentColor,
                      '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                    }}
                  >
                    Book Now
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </Container>
        </Box>

        {/* Image dots */}
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              display: 'flex',
              gap: 1,
            }}
          >
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: idx === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* About Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Reveal>
              <Typography variant="h4" fontWeight={600} gutterBottom>
                About Us
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {library.description || defaultDescription}
              </Typography>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={4}>
            <Reveal delay={0.12}>
              <Card
                elevation={0}
                sx={{
                  bgcolor: 'grey.100',
                  p: 3,
                  borderRadius: 3,
                  transition: 'box-shadow 0.3s',
                  '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.10)' },
                }}
              >
                {/* Seats */}
                {seatLabel && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <EventSeat sx={{ color: accentColor, fontSize: 40 }} />
                    <Box>
                      <Typography variant="h5" fontWeight={700}>
                        {availSeatsNum !== null ? (
                          <AnimatedNumber value={availSeatsNum} suffix={library.capacity ? ` / ${library.capacity} free` : ' seats'} />
                        ) : (
                          seatLabel
                        )}
                      </Typography>
                      <Typography color="text.secondary">Seats Available</Typography>
                    </Box>
                  </Box>
                )}

                {/* Hours */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: daysStr ? 2 : 0 }}>
                  <AccessTime sx={{ color: accentColor, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Open Daily</Typography>
                    <Typography color="text.secondary">
                      {hoursStr || 'Contact for hours'}
                    </Typography>
                  </Box>
                </Box>

                {/* Operational days */}
                {daysStr && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <CalendarToday sx={{ color: accentColor, fontSize: 28 }} />
                    <Typography color="text.secondary" variant="body2">{daysStr}</Typography>
                  </Box>
                )}

                {/* Lockers */}
                {library.locker_count > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <LockOutlined sx={{ color: accentColor, fontSize: 28 }} />
                    <Typography color="text.secondary" variant="body2">
                      {lockerNum !== null ? (
                        <AnimatedNumber value={lockerNum} suffix=" lockers" />
                      ) : (
                        `${library.locker_count} lockers`
                      )}
                    </Typography>
                  </Box>
                )}
              </Card>
            </Reveal>
          </Grid>
        </Grid>
      </Container>

      {/* Plans Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" sx={{ mb: 4 }}>
              Membership Plans
            </Typography>
          </Reveal>

          {plans.length > 0 ? (
            <MotionList
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 24,
              }}
            >
              {plans.map((plan) => {
                const planName = plan.name || titleCasePlanType(plan.plan_type);
                const price = formatPrice(plan.price_cents);
                const period = planPeriodLabel(plan.plan_type);
                const discPct = plan.first_time_discount;
                const discPrice = discountedFirstPrice(plan.price_cents, discPct);

                return (
                  <MotionItem key={plan.id}>
                    <Card
                      elevation={0}
                      sx={{
                        border: '2px solid',
                        borderColor: 'grey.200',
                        borderRadius: 3,
                        height: '100%',
                        position: 'relative',
                        transition: 'all 0.28s ease',
                        '&:hover': {
                          borderColor: accentColor,
                          transform: 'translateY(-6px)',
                          boxShadow: `0 12px 36px rgba(0,0,0,0.12)`,
                        },
                      }}
                    >
                      {/* Discount badge */}
                      {discPct > 0 && (
                        <Chip
                          label={`${discPct}% OFF first time`}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: accentColor,
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.65rem',
                          }}
                        />
                      )}

                      <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        {/* Plan name */}
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                          {planName}
                        </Typography>

                        {/* Price */}
                        {price && (
                          <>
                            {discPct > 0 && discPrice !== null ? (
                              <Box sx={{ mb: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textDecoration: 'line-through' }}
                                >
                                  {price}
                                </Typography>
                                <Typography variant="h4" fontWeight={700} sx={{ color: accentColor }}>
                                  {formatPrice(discPrice)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  first time, then {price}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="h3" fontWeight={700} sx={{ color: accentColor, mb: 0.5 }}>
                                {price}
                              </Typography>
                            )}
                            {period && (
                              <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
                                {period}
                              </Typography>
                            )}
                          </>
                        )}

                        {/* Plan features */}
                        <Box sx={{ textAlign: 'left', mb: 2 }}>
                          {plan.duration_days && (
                            <PlanFeature icon={<CalendarToday />} color="text.secondary">
                              Valid {plan.duration_days} days
                            </PlanFeature>
                          )}
                          <PlanFeature icon={<Schedule />} color="text.secondary">
                            {hoursLabel(plan.hours_per_day)}
                          </PlanFeature>
                          {plan.recurring && (
                            <PlanFeature icon={<Repeat />} color={accentColor} sx={{ mt: 0.5 }}>
                              Auto-renews
                            </PlanFeature>
                          )}
                        </Box>

                        {/* Description */}
                        {plan.description ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mb: 2,
                            }}
                          >
                            {plan.description}
                          </Typography>
                        ) : null}

                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => onBookNow(plan)}
                          sx={{
                            borderColor: accentColor,
                            color: accentColor,
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: accentColor, color: 'white' },
                          }}
                        >
                          Select Plan
                        </Button>
                      </CardContent>
                    </Card>
                  </MotionItem>
                );
              })}
            </MotionList>
          ) : (
            <Card elevation={0} sx={{ bgcolor: 'grey.100', p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>Contact us for membership details</Typography>
              <Button
                variant="contained"
                onClick={() => onBookNow()}
                sx={{ bgcolor: accentColor, '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' } }}
              >
                Get in Touch
              </Button>
            </Card>
          )}
        </Container>
      </Box>

      {/* Gallery Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Reveal>
          <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" sx={{ mb: 4 }}>
            Gallery
          </Typography>
        </Reveal>
        <MotionList
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 16,
          }}
        >
          {images.slice(0, 6).map((img, idx) => (
            <MotionItem key={idx}>
              <Box
                component="img"
                src={img}
                alt={`Gallery ${idx + 1}`}
                onClick={() => { setCurrentImageIndex(idx); setLightboxOpen(true); }}
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: 2,
                  cursor: 'pointer',
                  display: 'block',
                  transition: 'transform 0.28s ease, box-shadow 0.28s ease',
                  '&:hover': {
                    transform: 'scale(1.04)',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
                  },
                }}
              />
            </MotionItem>
          ))}
        </MotionList>
      </Container>

      {/* Amenities Section */}
      {library.amenities?.length > 0 && (
        <Box sx={{ bgcolor: 'white', py: 8 }}>
          <Container maxWidth="lg">
            <Reveal>
              <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" sx={{ mb: 4 }}>
                Amenities
              </Typography>
            </Reveal>
            <MotionList
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 16,
              }}
            >
              {library.amenities.map((amenity, idx) => (
                <MotionItem key={idx}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      transition: 'background-color 0.22s, transform 0.22s, box-shadow 0.22s',
                      '&:hover': {
                        bgcolor: `${accentColor}12`,
                        transform: 'translateY(-4px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
                      },
                    }}
                  >
                    <Box sx={{ color: accentColor, mb: 1 }}>
                      {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 40 } })}
                    </Box>
                    <Typography variant="body2" fontWeight={500}>
                      {amenity}
                    </Typography>
                  </Box>
                </MotionItem>
              ))}
            </MotionList>
          </Container>
        </Box>
      )}

      {/* Location Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Reveal>
          <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center" sx={{ mb: 4 }}>
            Location
          </Typography>
        </Reveal>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Reveal delay={0.08}>
              <Box
                sx={{
                  height: 300,
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'grey.200',
                }}
              >
                {library.lat && library.lng ? (
                  <iframe
                    title="Library Location"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">Map location unavailable</Typography>
                  </Box>
                )}
              </Box>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={6}>
            <Reveal delay={0.16}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <LocationOn sx={{ color: accentColor, mt: 0.5 }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Address</Typography>
                    <Typography color="text.secondary">{library.address}</Typography>
                  </Box>
                </Box>
                {library.contact_phone && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Phone sx={{ color: accentColor, mt: 0.5 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>Phone</Typography>
                      <Typography color="text.secondary">
                        <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_phone}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_email && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Email sx={{ color: accentColor, mt: 0.5 }} />
                    <Box>
                      <Typography variant="h6" fontWeight={600}>Email</Typography>
                      <Typography color="text.secondary">
                        <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_email}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Reveal>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: accentColor, py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Reveal>
            <Typography variant="h4" fontWeight={600} color="white" gutterBottom>
              Ready to Start Your Journey?
            </Typography>
            <Typography color="rgba(255,255,255,0.9)" sx={{ mb: 4 }}>
              Join {library.name} today and get access to a peaceful study environment.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => onBookNow()}
              sx={{
                bgcolor: 'white',
                color: accentColor,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                },
                px: 4,
                py: 1.5,
              }}
            >
              Book Now
            </Button>
          </Reveal>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1a1a1a', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography color="rgba(255,255,255,0.7)">
              {library.name}
            </Typography>
            <Typography color="rgba(255,255,255,0.5)" variant="body2">
              Powered by{' '}
              <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor }}>
                BookMyLibrary
              </a>
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'black' } }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <Box
          component="img"
          src={images[currentImageIndex]}
          alt="Gallery"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Dialog>
    </Box>
  );
}

export default ModernTheme;
